import React, {Component} from "react";
import moment from "moment";
import '../../EMR.css';
import DateUtil from "../../../../../utils/DateUtil";
import TreatmentReceivedEnum from "../enums/treatmentrecieved/TreatmentReceivedEnum";
import {MultiSelect} from "primereact/multiselect";
import {Dropdown} from "primereact/dropdown";
import RedAsterisk from "../../../../../components/asterisk/RedAsterisk";
import {getClinicTimeZone} from "../../../../../utils/TimezoneUtil";
import {
    ADDON_START_AFTER_BASE_END_MIN,
    BASE_INFUSION_CODES,
    gapAfterPredecessorEnd,
    INJECTION_DURATION_MIN,
    isAddon,
    isBaseInfusion,
    isInjection,
    isMedication,
    isTimed,
    IV_PUSH_INITIAL_CODE,
    predecessorsOf,
    SEQUENTIAL_GAP_MIN,
    START_AFTER_APPT_END_MIN,
    windowFor
} from "../TimedCptCodes";
import TimeInput from "../../../../../components/date-input/TimeInput";
import RemotingService from "../../../../../services/remoting-service/RemotingService";
import NotificationService from "../../../../../services/notification-service/NotificationService";
import {Button} from "react-bootstrap";

// Modifier billing codes carry no treatment-received timestamp (blank start/end, excluded from division).
const MODIFIER_CPT_CODES = ['Modifier 24', 'Modifier 25', 'Modifier 50', 'Modifier 52'];
const isModifier = (code) => MODIFIER_CPT_CODES.includes(code);

export default class TreatmentReceivedCptModal extends Component {

    constructor(props) {
        super(props);
        this.state = {
            procedureList: props.procedureList,
            editMode: false
        }
    }

    treatmentsChanged = (procedure, treatments) => {
        procedure.treatments = treatments;
        this.forceUpdate();
    }

    endChanged = (procedure, endTime) => {
        procedure.endTime = endTime;
        this.forceUpdate();
    }

    startChanged = (procedure, startTime) => {
        procedure.startTime = startTime;
        this.forceUpdate();
    }

    // The clinician picks a wall-clock time in the clinic zone; anchor it to the visit day.
    combineVisitDayTime = (pickedDate) => {
        if (!pickedDate) {
            return null;
        }
        const picked = moment(pickedDate);
        const visitDay = this.props.appointment ? this.props.appointment.startTime : null;
        return moment.tz(visitDay || undefined, getClinicTimeZone())
            .set({hour: picked.hour(), minute: picked.minute(), second: 0, millisecond: 0})
            .toDate();
    }

    // Show a stored instant as clinic-zone wall-clock inside the (browser-local) time picker.
    toClinicPickerValue = (instant) => {
        if (!instant) {
            return null;
        }
        const clinic = moment.tz(instant, getClinicTimeZone());
        return moment().set({hour: clinic.hour(), minute: clinic.minute(), second: 0, millisecond: 0}).toDate();
    }

    renderTimeInput = (procedure, value, onChange) =>
        <TimeInput value={this.toClinicPickerValue(value)}
                   onChange={e => onChange(procedure, this.combineVisitDayTime(e.value))}/>;

    // Timestamps become editable only while no claim has been generated (checked server-side first).
    enterEditMode = () => {
        const appointmentId = this.props.appointment ? this.props.appointment.id : null;
        if (!appointmentId) {
            this.activateEditMode();
            return;
        }
        RemotingService.getRemoteCall(`api/emr/${appointmentId}/timestamp-edit-status`, null, (status) => {
            if (status && status.editable === false) {
                NotificationService.showNotification({
                    severity: 'error',
                    summary: 'Editing not allowed',
                    detail: `A claim (${status.claimId || '-'}) already exists in status ${status.claimStatus || '-'}. Timestamps can no longer be edited.`
                });
                return;
            }
            this.activateEditMode();
        });
    }

    activateEditMode = () => {
        this.setState({editMode: true});
        if (this.props.onEnterEditMode) {
            this.props.onEnterEditMode();
        }
    }

    // Auto start (read-only): timed base/injection => appt end + 2; add-on => base end + 31;
    // non-timed => the start assigned by the backend on the previous save (may be empty until saved).
    computeStart = (procedure) => {
        const code = procedure.cpt.cptCode;
        if (isModifier(code) || isMedication(procedure.cpt)) {
            return null; // modifier & medication codes have no timestamp
        }
        if (isAddon(code)) {
            const baseEnd = this.latestBaseEnd();
            return baseEnd ? DateUtil.addMinutes(baseEnd, ADDON_START_AFTER_BASE_END_MIN) : null;
        }
        if (predecessorsOf(code).length > 0) {
            // 96375 continues at 96374's end; 96376 resumes 30 min after 96374/96375/96365.
            const predecessorEnd = this.latestEndOf(predecessorsOf(code));
            return predecessorEnd ? DateUtil.addMinutes(predecessorEnd, gapAfterPredecessorEnd(code)) : null;
        }
        if (isTimed(code)) {
            const apptEnd = this.props.appointment ? this.props.appointment.endTime : null;
            if (!apptEnd) {
                return null;
            }
            const firstTimedStart = DateUtil.addMinutes(apptEnd, START_AFTER_APPT_END_MIN);
            // When an injection (96372) is also present, the next timed code runs after it (no overlap).
            const afterInjectionStart = this.hasInjection()
                ? DateUtil.addMinutes(DateUtil.addMinutes(firstTimedStart, INJECTION_DURATION_MIN), SEQUENTIAL_GAP_MIN)
                : firstTimedStart;
            if (code === IV_PUSH_INITIAL_CODE) {
                return afterInjectionStart;
            }
            if (isBaseInfusion(code)) {
                // The infusion runs after a 96374 when one already has an end.
                const ivPushEnd = this.latestEndOf([IV_PUSH_INITIAL_CODE]);
                return ivPushEnd ? DateUtil.addMinutes(ivPushEnd, SEQUENTIAL_GAP_MIN) : afterInjectionStart;
            }
            return firstTimedStart;
        }
        const suggestion = this.nonTimedSuggestion(procedure);
        return procedure.startTime || (suggestion ? suggestion.start : null);
    }

    hasInjection = () => this.state.procedureList.some(p => isInjection(p.cpt.cptCode));

    latestBaseEnd = () => this.latestEndOf(BASE_INFUSION_CODES);

    // Latest recorded end among the given codes; used to chain codes that start from another's end.
    latestEndOf = (codes) => {
        const ends = this.state.procedureList
            .filter(p => codes.includes(p.cpt.cptCode) && p.endTime)
            .map(p => moment(p.endTime));
        return ends.length > 0 ? moment.max(ends).toDate() : null;
    }

    // Mirror the backend equal-division to suggest a start/end for a non-timed code.
    nonTimedSuggestion = (procedure) => {
        const appt = this.props.appointment;
        if (!appt || !appt.startTime || !appt.endTime) {
            return null;
        }
        const nonTimed = this.state.procedureList
            .filter(p => !isTimed(p.cpt.cptCode) && !isModifier(p.cpt.cptCode) && !isMedication(p.cpt))
            .sort((a, b) => String(a.cpt.cptCode).localeCompare(String(b.cpt.cptCode)));
        const index = nonTimed.indexOf(procedure);
        if (index < 0) {
            return null;
        }
        const start0 = moment(appt.startTime).add(1, 'minute');
        const period = moment(appt.endTime).diff(start0, 'minutes', true) / nonTimed.length;
        return {
            start: start0.clone().add(index * period, 'minutes').toDate(),
            end: start0.clone().add((index + 1) * period, 'minutes').toDate()
        };
    }

    // In edit mode the start column also becomes an editable time input (system-set values included).
    renderStartCell = (procedure, start, timeZone) => {
        const code = procedure.cpt.cptCode;
        if (isModifier(code) || isMedication(procedure.cpt)) {
            return '-'; // modifier & medication codes have no start time
        }
        if (this.state.editMode) {
            return this.renderTimeInput(procedure, procedure.startTime != null ? procedure.startTime : start, this.startChanged);
        }
        return start ? DateUtil.getHourMinute24H(start, timeZone) : '-';
    }

    renderEndPicker = (procedure, start, timeZone) => {
        const code = procedure.cpt.cptCode;

        if (isModifier(code) || isMedication(procedure.cpt)) {
            return <span>-</span>; // modifier & medication codes have no end time
        }

        if (this.state.editMode) {
            return this.renderTimeInput(procedure, procedure.endTime, this.endChanged);
        }

        if (!isTimed(code)) {
            // Non-timed codes follow the appointment-window division; end is system-set, not selectable.
            const suggestion = this.nonTimedSuggestion(procedure);
            const value = procedure.endTime || (suggestion ? suggestion.end : null);
            return <span className="text-nowrap font-weight-bold">
                {value ? DateUtil.getHourMinute24H(value, timeZone) : '-'}
            </span>;
        }

        if (!start) {
            if (isAddon(code)) {
                return <span className="text-danger">Select the base infusion code ({BASE_INFUSION_CODES.join(' or ')}) end time first</span>;
            }
            if (predecessorsOf(code).length > 0) {
                return <span className="text-danger">Select the {predecessorsOf(code).join(' or ')} end time first</span>;
            }
            return <span>-</span>;
        }

        // 96372 is always recorded as a fixed 4 min (read-only, no selection).
        if (isInjection(code)) {
            const end = DateUtil.addMinutes(start, INJECTION_DURATION_MIN);
            return <span className="text-nowrap font-weight-bold">{DateUtil.getHourMinute24H(end, timeZone)}</span>;
        }

        const window = windowFor(code);
        if (!window) {
            // Timed code without a configured duration window: show the stored end, don't offer a picker.
            return <span className="text-nowrap font-weight-bold">
                {procedure.endTime ? DateUtil.getHourMinute24H(procedure.endTime, timeZone) : '-'}
            </span>;
        }
        const options = [];
        for (let minutes = window.min; minutes <= window.max; minutes++) {
            const candidate = DateUtil.addMinutes(start, minutes);
            options.push({label: DateUtil.getHourMinute24H(candidate, timeZone), value: minutes});
        }
        const selected = procedure.endTime ? DateUtil.diff(start, procedure.endTime, 'minute') : null;

        return <Dropdown value={selected}
                         options={options}
                         placeholder="Select..."
                         onChange={e => this.endChanged(procedure, DateUtil.addMinutes(start, e.value))}/>;
    }

    render() {
        const receivedTreatmentsWithData = TreatmentReceivedEnum.toArray()
            .filter(treatment => this.props.treatmentReceived[treatment.field]?.length > 0);
        const receivedTreatmentTexts = receivedTreatmentsWithData.map(t => t.key);
        const timeZone = getClinicTimeZone();
        return (
            <div>
                {receivedTreatmentsWithData.length <= 0 && <><RedAsterisk renderForAllTypes/> Please add treatment(s)
                    first</>}
                <div className={"content-card"}>
                    {!this.state.editMode &&
                        <div style={{marginBottom: '10px', textAlign: 'right'}}>
                            <Button variant="warning" size="sm" onClick={this.enterEditMode}>Edit timestamps</Button>
                        </div>}
                    <table className={"emr-table"}>
                        <tbody>
                        <tr>
                            <th>Code</th>
                            <th>Start</th>
                            <th>End</th>
                            <th>Treatment Received</th>
                        </tr>
                        {this.state.procedureList?.length > 0 ?
                            this.state.procedureList.map((procedure, index) => {

                                const availableTreatments = procedure.treatments?.filter(t => receivedTreatmentTexts.includes(t));
                                const start = this.computeStart(procedure);
                                return <tr key={"procedures" + index} style={{backgroundColor: "white"}}>
                                    <td><span
                                        className="font-weight-bold">{procedure.cpt.cptCode}</span> - {procedure.cpt.description}
                                    </td>
                                    <td className="text-nowrap font-weight-bold">
                                        {this.renderStartCell(procedure, start, timeZone)}
                                    </td>
                                    <td>{this.renderEndPicker(procedure, start, timeZone)}</td>
                                    <td><MultiSelect value={availableTreatments}
                                                     options={receivedTreatmentsWithData}
                                                     optionLabel="name" optionValue="key"
                                                     disabled={receivedTreatmentsWithData.length <= 0}
                                                     placeholder={receivedTreatmentsWithData.length > 0 ? "Select..." : "N/A"}
                                                     onChange={e => this.treatmentsChanged(procedure, e.value)}/>
                                    </td>
                                </tr>
                            }
                            ) : "No cpts selected for billing in Diagnosis section"}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    }
}
